import React, { type FC, type ReactNode, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';

import Box from '../../components/box/box';

export type CollectionJustifyType = 'start' | 'center' | 'end' | 'full';
export type CollectionSpaceType = string | number | Record<string | number | symbol, ReactNode>;

export interface InnerCollectionProps {
  /** The horizontal margin between items. */
  mx?: number;
  /** The vertical margin between items. */
  my?: number;
  /** Sets the JustifyContent for the items. */
  justifyContent?: CollectionJustifyType;
}

export type CollectionProps = {
  /** The children passed to the Collection component. */
  children?: ReactNode | ReactNode[] | JSX.Element | JSX.Element[];
  /** Allows the component to get additional styles. */
  className?: string;
  /** The vertical justification for the Collection inner items. */
  justifyInnerItem?: CollectionJustifyType;
} & InnerCollectionProps;

const InnerCollection = styled.div<InnerCollectionProps>(({ justifyContent, mx = 0, my = 0, theme }) => [
  {
    label: 'innerCollection',
    display: 'flex',
    flexWrap: 'wrap',
    margin: `-${theme.space[my]}px 0 0 -${theme.space[mx]}px`,
    justifyContent: 'flex-end',
  },
  (justifyContent === 'start' || justifyContent === 'full') && {
    justifyContent: 'flex-start',
  },
  justifyContent === 'center' && {
    justifyContent: 'center',
  },
]);

const InnerItem = styled.div<InnerCollectionProps>(({ justifyContent, mx = 0, my = 0, theme }) => [
  {
    label: 'innerItem',
    margin: `${theme.space[my]}px 0 0 ${theme.space[mx]}px`,
    flex: 'initial',
    display: 'flex',
  },
  justifyContent === 'full' && {
    flex: 'auto',
  },
]);

const InnerJustifyItem = styled.div<{ justifyInnerItem?: CollectionJustifyType }>(({ justifyInnerItem }) => [
  {
    label: 'innerJustifyItem',
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    marginTop: 0,
    marginBottom: 0,
  },
  justifyInnerItem === 'end' ||
    (justifyInnerItem === 'center' && {
      marginTop: 'auto',
    }),
  justifyInnerItem === 'start' ||
    (justifyInnerItem === 'center' && {
      marginBottom: 'auto',
    }),
]);

const Collection: FC<PropsWithChildren<CollectionProps>> = ({
  className,
  children,
  mx = 0,
  my = 0,
  justifyContent = 'start',
  justifyInnerItem = 'start',
}) => (
  <Box
    css={{
      label: 'collection',
    }}
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
  >
    <InnerCollection justifyContent={justifyContent} mx={mx} my={my}>
      {React.Children.map(children, child => (
        <InnerItem justifyContent={justifyContent} mx={mx} my={my}>
          <InnerJustifyItem justifyInnerItem={justifyInnerItem}>{child}</InnerJustifyItem>
        </InnerItem>
      ))}
    </InnerCollection>
  </Box>
);

export { Collection };
export default Collection;
